import * as Dialog from "@radix-ui/react-dialog";
import { TbX, TbInfoCircle } from "react-icons/tb";
import { getVersionInfo } from "@/lib/version";
import { useMemo } from "react";

interface AboutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AboutModal: React.FC<AboutModalProps> = ({
  open,
  onOpenChange,
}) => {
  const versionInfo = getVersionInfo();

  // Enhanced platform detection - memoized to prevent creating multiple WebGL contexts
  const platformInfo = useMemo(() => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;

    // Detect Apple Silicon vs Intel Mac
    if (platform === "MacIntel") {
      // Check for Apple Silicon indicators
      if (userAgent.includes("Mac") && navigator.maxTouchPoints > 0) {
        return "macOS (Apple Silicon)";
      }

      // Additional check using WebGL renderer if available
      try {
        const canvas = document.createElement("canvas");
        const gl = canvas.getContext("webgl") as WebGLRenderingContext | null;
        if (gl) {
          const renderer = gl.getParameter(gl.RENDERER) as string;
          // Clean up the WebGL context immediately after use
          const loseContext = gl.getExtension("WEBGL_lose_context");
          if (loseContext) {
            loseContext.loseContext();
          }

          if (
            renderer.includes("Apple") ||
            renderer.includes("M1") ||
            renderer.includes("M2") ||
            renderer.includes("M3")
          ) {
            return "macOS (Apple Silicon)";
          }
        }
      } catch {
        // WebGL not available, continue with other detection
      }

      return "macOS (Intel)";
    }

    // Other platform detection
    if (platform.includes("Win")) return "Windows";
    if (platform.includes("Linux")) return "Linux";
    if (platform.includes("Mac")) return "macOS";

    return platform || "Unknown";
  }, []); // Empty dependency array - only compute once

  const browserInfo = useMemo(() => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Chrome") && !userAgent.includes("Edg"))
      return "Chrome";
    if (userAgent.includes("Firefox")) return "Firefox";
    if (userAgent.includes("Safari") && !userAgent.includes("Chrome"))
      return "Safari";
    if (userAgent.includes("Edg")) return "Edge";
    return "Unknown";
  }, []); // Empty dependency array - only compute once

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-gray-800/50 z-[1100]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-full max-w-md z-[1101]">
          <div className="flex flex-col gap-6 items-start justify-start p-6 w-full">
            {/* Header */}
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <TbInfoCircle size={20} className="text-blue-600" />
                </div>
                <Dialog.Title className="font-['Open_Sans'] font-bold text-[#444444] text-[18px]">
                  About Taskr
                </Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                  <TbX size={16} className="text-gray-500" />
                </button>
              </Dialog.Close>
            </div>

            {/* Content */}
            <div className="flex flex-col gap-4 w-full">
              {/* Logo and App Name */}
              <div className="flex flex-col items-center gap-4 py-4">
                <div
                  className="w-16 h-16 bg-no-repeat bg-center bg-contain"
                  style={{
                    backgroundImage: `url('images/logo.svg')`,
                  }}
                />
                <div className="text-center">
                  <h2 className="font-['Open_Sans'] font-bold text-[24px] text-[#444444]">
                    Taskr
                  </h2>
                  <p className="text-[#828282] text-[14px] mt-1">
                    Task Management & Asset Tracking System
                  </p>
                </div>
              </div>

              {/* Version Information */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="font-['Open_Sans'] font-semibold text-[14px] text-[#444444] mb-2">
                  Version Information
                </h3>
                <div className="space-y-2 text-[12px]">
                  <div className="flex justify-between">
                    <span className="text-[#828282]">Version:</span>
                    <span className="font-medium text-[#444444]">
                      v{versionInfo.version}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#828282]">Environment:</span>
                    <span className="font-medium text-[#444444] capitalize">
                      {versionInfo.environment}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#828282]">Build Date & Time:</span>
                    <span className="font-medium text-[#444444]">
                      {versionInfo.buildDateTime}
                    </span>
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h3 className="font-['Open_Sans'] font-semibold text-[14px] text-[#444444] mb-2">
                  System Information
                </h3>
                <div className="space-y-1 text-[12px]">
                  <div className="flex justify-between">
                    <span className="text-[#828282]">Browser:</span>
                    <span className="font-medium text-[#444444]">
                      {browserInfo}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#828282]">Platform:</span>
                    <span className="font-medium text-[#444444]">
                      {platformInfo}
                    </span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-[11px] text-[#828282] pt-2">
                © 2025 Taskr. All rights reserved.
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
